import { useCallback, useMemo, useRef } from "react";
import { useUser, patchCachedUserFavourites } from "./UserContext";
import { getAuthHeaders } from "../utils/authHeaders";
import {
  buildFavouriteTeamToggle,
  isFavouriteTeam,
  parseCommaList,
  parseFavTeamsFromStorage,
  type Favourites,
} from "../utils/favouriteTeams";
import { canonicalTeamSlug, resolveCanonicalTeam } from "../utils/cricketTeamCanonical";
import {
  PROFILE_FAV_IPL_TEAM_OPTIONS,
  favIplLabel,
  parseFavIplFromStorage,
} from "../components/Profile/profileFavIplTeams";
import {
  PROFILE_FAV_CRICKET_INT_OPTIONS,
  favCricketIntLabel,
  parseFavCricketIntFromStorage,
} from "../components/Profile/profileFavCricketIntTeams";
import type { ProfileFavTeamExtra } from "../components/Profile/ProfileFavouriteCricketTeamsGrid";

const IPL_CATALOG = new Set<string>(PROFILE_FAV_IPL_TEAM_OPTIONS.map((t) => t.slug));
const INT_CATALOG = new Set<string>(PROFILE_FAV_CRICKET_INT_OPTIONS.map((t) => t.slug));

/**
 * Starred / stored favourites that are not in the curated Club teams (IPL) or
 * International pickers — shown as selected extra tiles in those sections.
 */
export function profileFavouriteTeamExtras(favourites: Favourites | null | undefined): {
  clubExtras: ProfileFavTeamExtra[];
  intExtras: ProfileFavTeamExtra[];
} {
  const clubExtras: ProfileFavTeamExtra[] = [];
  const intExtras: ProfileFavTeamExtra[] = [];
  const seenClub = new Set<string>();
  const seenInt = new Set<string>();

  const pushClub = (slug: string, name: string, label?: string) => {
    if (!slug || seenClub.has(slug) || IPL_CATALOG.has(slug)) return;
    seenClub.add(slug);
    clubExtras.push({ slug, name, label });
  };
  const pushInt = (slug: string, name: string, label?: string) => {
    if (!slug || seenInt.has(slug) || INT_CATALOG.has(slug)) return;
    seenInt.add(slug);
    intExtras.push({ slug, name, label });
  };

  for (const slug of parseFavIplFromStorage(favourites?.faviplteams)) {
    if (!IPL_CATALOG.has(slug)) {
      const label = favIplLabel(slug);
      pushClub(slug, label, label);
    }
  }
  for (const slug of parseFavCricketIntFromStorage(favourites?.favnationalteams)) {
    if (!INT_CATALOG.has(slug)) {
      // Prefer title-cased slug for logo lookup when not in the curated picker.
      const label = favCricketIntLabel(slug);
      const display =
        label === slug.replace(/-/g, " ")
          ? slug
              .split("-")
              .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
              .join(" ")
          : label;
      pushInt(slug, display, display);
    }
  }

  const iplStore = new Set(parseCommaList(favourites?.faviplteams).map(canonicalTeamSlug));
  const natStore = new Set(parseCommaList(favourites?.favnationalteams).map(canonicalTeamSlug));

  for (const name of parseFavTeamsFromStorage(favourites?.favcustomcricketteams)) {
    const { slug, category } = resolveCanonicalTeam(name);
    if (!slug || iplStore.has(slug) || natStore.has(slug)) continue;
    if (category === "national" || INT_CATALOG.has(slug)) {
      pushInt(slug, name);
    } else if (category === "ipl" || IPL_CATALOG.has(slug)) {
      // Curated IPL tile covers selection; skip duplicate extra.
      continue;
    } else {
      pushClub(slug, name);
    }
  }

  return { clubExtras, intExtras };
}

/**
 * Read/toggle the logged-in user's favourite teams across all three stores
 * (IPL picker, national picker, custom). Optimistically updates UserContext + cache,
 * then persists via `POST /account/favourite-teams/toggle`.
 * Guests cannot toggle here — FavouriteTeamStar sends them to sign up instead.
 */
export function useFavouriteTeams() {
  const { user, setUser } = useUser();
  const inFlight = useRef<Set<string>>(new Set());

  const favourites = (user?.favourites ?? null) as Favourites | null;

  const { clubExtras, intExtras } = useMemo(
    () => profileFavouriteTeamExtras(favourites),
    [favourites]
  );

  /** @deprecated Prefer clubExtras / intExtras — custom-only names for legacy callers. */
  const favTeams = useMemo(
    () => [...clubExtras, ...intExtras].map((e) => e.name),
    [clubExtras, intExtras]
  );

  const canFavourite = Boolean(user?.userId);

  const isFavourite = useCallback(
    (teamName: string) => isFavouriteTeam(favourites, teamName),
    [favourites]
  );

  const toggle = useCallback(
    async (teamName: string): Promise<boolean | null> => {
      const name = String(teamName || "").trim();
      const slug = resolveCanonicalTeam(name).slug;
      if (!name || !slug || !user?.userId) return null;
      if (inFlight.current.has(slug)) return null;

      const plan = buildFavouriteTeamToggle(favourites, name);
      if (!plan) return null;
      inFlight.current.add(slug);

      const previous: Favourites = { ...(favourites ?? {}) };
      const applyFavourites = (next: Favourites) => {
        setUser((prev) => (prev ? { ...prev, favourites: { ...next } } : prev));
        patchCachedUserFavourites(next);
      };

      applyFavourites(plan.next);

      try {
        const res = await fetch(`${process.env.API_URL}/account/favourite-teams/toggle`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ ops: plan.ops }),
        });
        if (!res.ok) throw new Error(`toggle failed: ${res.status}`);
        const data = (await res.json()) as { favourites?: Partial<Favourites>; favourited?: boolean };
        if (data.favourites && typeof data.favourites === "object") {
          applyFavourites({ ...plan.next, ...data.favourites });
        }
        return Boolean(data.favourited);
      } catch {
        applyFavourites(previous);
        return null;
      } finally {
        inFlight.current.delete(slug);
      }
    },
    [favourites, setUser, user?.userId]
  );

  return { favTeams, clubExtras, intExtras, canFavourite, isFavourite, toggle };
}
